import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { localISODate, todayISO } from '../utils/dates';
import HistoryButton from './HistoryButton';

import DateInput from './DateInput';
const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};
const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const qty = (v) => (v == null || v === '' ? '' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 }));
const num = (v) => Number(v || 0);

const OUT_REASONS = [
  ['DAMAGED', 'Damaged'],
  ['EXPIRED', 'Expired'],
  ['LOST', 'Lost or stolen'],
  ['SAMPLE', 'Given as samples'],
  ['OWN_USE', 'Used in the business'],
  ['OTHER', 'Other']
];
const IN_REASONS = [['FOUND', 'Found'], ['OTHER', 'Other']];
const MODES = {
  OUT: { title: 'Write off stock', hint: 'Enter how much of each lot to take off the books.', column: 'Take off' },
  IN: { title: 'Add stock found', hint: 'Enter how much more of each lot is there than the books say. It comes in at that lot\'s cost.', column: 'Add' },
  COUNT: { title: 'Stock count', hint: 'Enter what is on the shelf for each lot you count. Lots left empty are not changed.', column: 'Counted' }
};

/**
 * Inventory > Stock adjustments: stock written off (damaged, expired, lost, samples, own use) or found, and stock
 * counts that post the differences from the books. Storekeepers see the list and print a count sheet; inventory
 * managers post and undo. Each adjustment takes stock out on a GIN from its lot, or brings it in on a GRN with no
 * vendor; the books' closing stock (and so the profit) moves with it.
 */
function StockAdjustments({ active, locations = [], canPost = false, currency = 'INR' }) {
  const [range, setRange] = useState(() => {
    const now = new Date();
    return [localISODate(new Date(now.getFullYear(), now.getMonth(), 1)), todayISO()];
  });
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null); // OUT, IN, COUNT, or SHEET (print only)
  const [viewing, setViewing] = useState(null);

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: 2 });
    return (v) => f.format(num(v));
  }, [currency]);

  const load = useCallback(() => {
    axios.get('/api/inventory/stock-adjustments', { params: { from: range[0], to: range[1] } })
      .then((r) => setData(r.data))
      .catch((error) => toast.error(errorMessage(error, 'Could not load stock adjustments')));
  }, [range]);

  useEffect(() => { if (active) load(); }, [active, load]);

  if (!active) return null;

  const open = async (a) => {
    try {
      const { data: d } = await axios.get(`/api/inventory/stock-adjustments/${a.id}`);
      setViewing(d);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not open it'));
    }
  };

  const undo = async (a) => {
    const reason = window.prompt(`Undo ${a.number}? The stock goes back to what it was before. Why? (optional)`);
    if (reason === null) return;
    try {
      await axios.post(`/api/inventory/stock-adjustments/${a.id}/undo`, { reason });
      toast.success(`${a.number} undone`);
      setViewing(null);
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not undo it'));
    }
  };

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <p className="text-muted mb-0">Stock written off or found, and stock counts. The books&apos; stock and profit move with them.</p>
        <div className="d-flex flex-wrap gap-2">
          <Button variant="outline-secondary" size="sm" onClick={() => setMode('SHEET')}>
            <i className="bi bi-printer me-1" />Count sheet
          </Button>
          {canPost && (
            <>
              <Button variant="outline-danger" size="sm" onClick={() => setMode('OUT')}>Write off stock</Button>
              <Button variant="outline-success" size="sm" onClick={() => setMode('IN')}>Add stock found</Button>
              <Button variant="primary" size="sm" onClick={() => setMode('COUNT')}>Stock count</Button>
            </>
          )}
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <DateInput size="sm" value={range[0]} max={range[1]} style={{ width: 'auto' }} onChange={(e) => setRange([e.target.value, range[1]])} />
        <span className="small text-muted">to</span>
        <DateInput size="sm" value={range[1]} min={range[0]} style={{ width: 'auto' }} onChange={(e) => setRange([range[0], e.target.value])} />
        {data && (
          <span className="ms-md-3 small">
            Written off <strong className="text-danger">{money(data.valueOut)}</strong> · Found <strong className="text-success">{money(data.valueIn)}</strong>
          </span>
        )}
      </div>
      <Table striped bordered hover responsive size="sm" className="small">
        <thead>
          <tr><th>Date</th><th>No.</th><th>Location</th><th>Why</th><th className="text-end">Written off</th><th className="text-end">Found</th><th>By</th><th /></tr>
        </thead>
        <tbody>
          {data && data.adjustments.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-3">No stock adjustments in this period.</td></tr>}
          {data && data.adjustments.map((a) => (
            <tr key={a.id} className={a.status === 'UNDONE' ? 'text-muted' : ''} style={{ cursor: 'pointer' }} onClick={() => open(a)}>
              <td className="text-nowrap">{day(a.date)}</td>
              <td className="font-monospace text-nowrap">{a.number}</td>
              <td>{a.locationName}</td>
              <td>
                {a.reasonLabel}{a.kind === 'COUNT' && a.linesCounted ? ` (${a.linesCounted} lots counted)` : ''}
                {a.notes && <div className="text-muted">{a.notes}</div>}
              </td>
              <td className="text-end text-nowrap">{num(a.valueOut) > 0 ? money(a.valueOut) : ''}</td>
              <td className="text-end text-nowrap">{num(a.valueIn) > 0 ? money(a.valueIn) : ''}</td>
              <td>{a.createdBy}</td>
              <td className="text-end" onClick={(e) => e.stopPropagation()}>
                {a.status === 'UNDONE'
                  ? <Badge bg="secondary" title={a.undoReason || ''}>Undone</Badge>
                  : canPost && <Button size="sm" variant="link" className="p-0" onClick={() => undo(a)}>Undo</Button>}
                <HistoryButton documentType="StockAdjustment" documentId={a.id} label={`Stock adjustment ${a.number}`} className="ms-1" />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {mode && (
        <AdjustmentForm mode={mode} locations={locations} money={money} onHide={() => setMode(null)}
          onPosted={() => { setMode(null); load(); }} />
      )}

      <Modal show={Boolean(viewing)} onHide={() => setViewing(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title as="h5">{viewing?.number} · {viewing?.reasonLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewing && (
            <>
              <div className="small mb-2">
                {day(viewing.date)} · {viewing.locationName} · by {viewing.createdBy}
                {viewing.ginNumber && <> · out on <span className="font-monospace">{viewing.ginNumber}</span></>}
                {viewing.grnNumber && <> · in on <span className="font-monospace">{viewing.grnNumber}</span></>}
              </div>
              {viewing.notes && <p className="small">{viewing.notes}</p>}
              {viewing.status === 'UNDONE' && (
                <Alert variant="secondary" className="small py-2">Undone by {viewing.undoneBy}{viewing.undoReason ? `: ${viewing.undoReason}` : ''}</Alert>
              )}
              <Table size="sm" bordered className="small mb-0">
                <thead><tr><th>Item</th><th>Lot</th><th className="text-end">Change</th><th className="text-end">Value</th></tr></thead>
                <tbody>
                  {(viewing.lines || []).map((l, i) => (
                    <tr key={i}>
                      <td>{l.itemName}</td>
                      <td className="font-monospace">{l.lotNumber}</td>
                      <td className={`text-end text-nowrap ${num(l.change) < 0 ? 'text-danger' : 'text-success'}`}>{num(l.change) > 0 ? '+' : ''}{qty(l.change)} {l.unit || ''}</td>
                      <td className="text-end text-nowrap">{money(l.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {viewing && viewing.status !== 'UNDONE' && canPost && <Button variant="outline-danger" onClick={() => undo(viewing)}>Undo</Button>}
          <Button variant="secondary" onClick={() => setViewing(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

/** Writing off, adding or counting: the lots the books hold at a location, with a quantity to enter against each. */
function AdjustmentForm({ mode, locations, money, onHide, onPosted }) {
  const printOnly = mode === 'SHEET';
  const m = MODES[mode] || MODES.COUNT;
  const [location, setLocation] = useState('');
  // With only one location there is nothing to choose
  useEffect(() => {
    if (locations.length === 1) setLocation((l) => l || `${locations[0].type}:${locations[0].id}`);
  }, [locations]);
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState({});
  const [date, setDate] = useState(todayISO());
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSheet(null);
    setEntries({});
    if (!location) return;
    const [type, id] = location.split(':');
    setLoading(true);
    axios.get('/api/inventory/stock-adjustments/sheet', { params: { locationType: type, locationId: id } })
      .then((r) => setSheet(r.data))
      .catch((error) => toast.error(errorMessage(error, 'Could not load the stock at that location')))
      .finally(() => setLoading(false));
  }, [location]);

  const lots = useMemo(() => {
    const all = sheet?.lots || [];
    const f = filter.trim().toLowerCase();
    return f ? all.filter((l) => `${l.itemName} ${l.lotNumber || ''} ${l.vendorLotNumber || ''}`.toLowerCase().includes(f)) : all;
  }, [sheet, filter]);

  // The change each entry makes to the books: taken off, added, or counted less the books
  const changeOf = (lot) => {
    const v = entries[lot.lotId];
    if (v === undefined || v === '') return null;
    if (mode === 'COUNT') return num(v) - num(lot.book);
    return mode === 'OUT' ? -num(v) : num(v);
  };
  const entered = (sheet?.lots || []).filter((l) => entries[l.lotId] !== undefined && entries[l.lotId] !== '');
  const valueOut = entered.reduce((s, l) => { const c = changeOf(l); return c < 0 ? s - c * num(l.unitCost) : s; }, 0);
  const valueIn = entered.reduce((s, l) => { const c = changeOf(l); return c > 0 ? s + c * num(l.unitCost) : s; }, 0);

  const post = async () => {
    if (entered.length === 0) { toast.warn(mode === 'COUNT' ? 'Enter what you counted' : 'Enter a quantity against at least one lot'); return; }
    if (mode !== 'COUNT' && !reason) { toast.warn('Choose why'); return; }
    const over = mode === 'OUT' && entered.find((l) => num(entries[l.lotId]) > num(l.book));
    if (over) { toast.warn(`Lot ${over.lotNumber} of ${over.itemName} has only ${qty(over.book)} on the books`); return; }
    const [type, id] = location.split(':');
    const lines = entered.map((l) => (mode === 'COUNT'
      ? { lotId: l.lotId, counted: num(entries[l.lotId]) }
      : { lotId: l.lotId, quantity: changeOf(l) }));
    if (mode !== 'COUNT' && lines.some((l) => !(Math.abs(l.quantity) > 0))) { toast.warn('Quantities must be more than zero'); return; }
    const summary = mode === 'COUNT'
      ? `Post this count? Written off ${money(valueOut)}, found ${money(valueIn)}.`
      : `${m.title}: ${money(mode === 'OUT' ? valueOut : valueIn)}?`;
    if (!window.confirm(summary)) return;
    setSaving(true);
    try {
      const { data } = await axios.post('/api/inventory/stock-adjustments', {
        kind: mode === 'COUNT' ? 'COUNT' : 'ADJUSTMENT', date, locationType: type, locationId: Number(id),
        reason: mode === 'COUNT' ? 'COUNT' : reason, notes, lines
      });
      toast.success(`${data.number} posted`);
      onPosted();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not post it'));
    } finally {
      setSaving(false);
    }
  };

  const locationName = locations.find((l) => `${l.type}:${l.id}` === location)?.name || '';

  return (
    <Modal show onHide={onHide} size="xl" className={printOnly ? 'print-modal' : ''}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .count-sheet, .count-sheet * { visibility: visible; }
          .count-sheet { position: absolute; left: 0; top: 0; width: 100%; }
          .count-sheet .no-print { display: none !important; }
          .count-sheet input { border: none; }
        }
      `}</style>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{printOnly ? 'Count sheet' : m.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-2 mb-2">
          <Col md={4}>
            <Form.Label className="small mb-1">Location</Form.Label>
            <Form.Select size="sm" value={location} onChange={(e) => setLocation(e.target.value)}>
              <option value="">Choose</option>
              {locations.map((l) => <option key={`${l.type}:${l.id}`} value={`${l.type}:${l.id}`}>{l.name}</option>)}
            </Form.Select>
          </Col>
          {!printOnly && (
            <>
              <Col md={2}>
                <Form.Label className="small mb-1">Date</Form.Label>
                <DateInput size="sm" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} />
              </Col>
              {mode !== 'COUNT' && (
                <Col md={2}>
                  <Form.Label className="small mb-1">Why</Form.Label>
                  <Form.Select size="sm" value={reason} onChange={(e) => setReason(e.target.value)}>
                    <option value="">Choose</option>
                    {(mode === 'OUT' ? OUT_REASONS : IN_REASONS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </Form.Select>
                </Col>
              )}
              <Col md={mode === 'COUNT' ? 6 : 4}>
                <Form.Label className="small mb-1">Note (optional)</Form.Label>
                <Form.Control size="sm" value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)}
                  placeholder={mode === 'COUNT' ? 'e.g. Month-end count by Ramesh' : 'e.g. Bags torn in the rain'} />
              </Col>
            </>
          )}
        </Row>
        {!printOnly && <p className="small text-muted mb-2">{m.hint}</p>}
        {mode === 'OUT' && reason === 'SAMPLE' && (
          <Alert variant="info" className="small py-2">GST paid on goods given away as free samples, lost or destroyed can&apos;t be claimed: ask your accountant to reverse that input tax credit.</Alert>
        )}

        {loading && <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>}
        {sheet && (
          <div className="count-sheet">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <div>
                <strong>{printOnly ? 'Stock count sheet' : m.title}: {locationName}</strong>
                <span className="small text-muted ms-2">{day(todayISO())}</span>
              </div>
              <Form.Control size="sm" className="no-print" style={{ maxWidth: 260 }} placeholder="Find an item or lot" value={filter} onChange={(e) => setFilter(e.target.value)} />
            </div>
            {sheet.lots.length === 0 ? (
              <p className="text-muted small">The books hold no stock at this location.</p>
            ) : (
              <Table size="sm" bordered className="small mb-2">
                <thead>
                  <tr>
                    <th>Item</th><th>Lot</th><th>Received</th><th className="text-end">Books</th>
                    <th style={{ width: 130 }}>{printOnly ? 'Counted' : m.column}</th>
                    {!printOnly && <th className="text-end">Difference</th>}
                    {!printOnly && <th className="text-end">Value</th>}
                  </tr>
                </thead>
                <tbody>
                  {lots.map((l) => {
                    const c = printOnly ? null : changeOf(l);
                    return (
                      <tr key={l.lotId}>
                        <td>{l.itemName}</td>
                        <td className="font-monospace">{l.lotNumber}{l.vendorLotNumber ? <div className="text-muted">{l.vendorLotNumber}</div> : null}</td>
                        <td className="text-nowrap">{day(l.receivedOn)}</td>
                        <td className="text-end text-nowrap">{qty(l.book)} {l.unit || ''}</td>
                        <td>
                          {printOnly ? <span>&nbsp;</span> : (
                            <Form.Control size="sm" type="number" min="0" step="any" value={entries[l.lotId] ?? ''}
                              onChange={(e) => setEntries({ ...entries, [l.lotId]: e.target.value })} />
                          )}
                        </td>
                        {!printOnly && (
                          <td className={`text-end text-nowrap ${c < 0 ? 'text-danger' : c > 0 ? 'text-success' : ''}`}>
                            {c == null ? '' : c === 0 ? 'matches' : `${c > 0 ? '+' : ''}${qty(c)} ${l.unit || ''}`}
                          </td>
                        )}
                        {!printOnly && <td className="text-end text-nowrap">{c ? money(Math.abs(c) * num(l.unitCost)) : ''}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
            {sheet.awaitingQc?.length > 0 && (
              <p className="small text-muted">
                Not listed: {sheet.awaitingQc.length} lot{sheet.awaitingQc.length === 1 ? '' : 's'} still waiting for quality control
                ({sheet.awaitingQc.map((l) => `${l.itemName} ${l.lotNumber}`).join(', ')}).
              </p>
            )}
            {printOnly && <p className="small">Counted by: ____________________ &nbsp; Date: ____________ &nbsp; Checked by: ____________________</p>}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div className="small">
          {!printOnly && (valueOut > 0 || valueIn > 0) && (
            <>
              {valueOut > 0 && <span className="text-danger me-3">Written off {money(valueOut)}</span>}
              {valueIn > 0 && <span className="text-success">Found {money(valueIn)}</span>}
            </>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onHide}>{printOnly ? 'Close' : 'Cancel'}</Button>
          {printOnly
            ? <Button variant="primary" disabled={!sheet} onClick={() => window.print()}><i className="bi bi-printer me-1" />Print</Button>
            : <Button variant={mode === 'OUT' ? 'danger' : 'primary'} disabled={!sheet || saving} onClick={post}>{saving ? 'Posting...' : 'Post'}</Button>}
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default StockAdjustments;
